import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getPurchaseHistory } from "@/server/api/get-purchase-history";
import {
  useHistoryPeriod,
  type HistoryPeriodOption,
} from "@/composables/history/useHistoryPeriod";

type Category = "main" | "additional" | "financial";
type FinanceTabKey = "all" | "minus" | "plus";
type OperationType = "minus" | "plus";

interface HistoryTab {
  key: FinanceTabKey;
  title: string;
  active: boolean;
}

interface CategoryTab {
  key: Category;
  label: string;
  class: string;
}

interface PurchaseHistoryItem {
  chargedAmount: number | string;
  purchasedOn: string;
  type: string;
  product: {
    category: Category;
    internal_name: string;
    name: string;
  };
}

interface FinanceOperation {
  amount: number;
  category: Category;
  internal_name: string;
  name: string;
  time: string;
  type: OperationType;
}

interface FinanceOperationGroup {
  date: string;
  operations: FinanceOperation[];
}

export function useHistoryFinances() {
  const { t } = useI18n();
  const {
    choosePeriod,
    formatDateToHuman,
    getPeriodStartEnd,
    isCustomPeriod,
    isOpenedSelect,
    openSelect,
    periodOptions,
    periodText,
    resetPeriod,
    selectedPeriod,
  } = useHistoryPeriod();

  const activeTab = ref<FinanceTabKey>("all");
  const activeCategory = ref<Category | null>(null);
  const apiResponse = ref<PurchaseHistoryItem[]>([]);
  const categoryTotals = ref<Record<Category, number>>({
    main: 0,
    additional: 0,
    financial: 0,
  });

  const operationsTabs = computed<HistoryTab[]>(() => [
    {
      key: "all",
      title: t("history.finances.tabs.all"),
      active: activeTab.value === "all",
    },
    {
      key: "minus",
      title: t("history.finances.tabs.minus"),
      active: activeTab.value === "minus",
    },
    {
      key: "plus",
      title: t("history.finances.tabs.plus"),
      active: activeTab.value === "plus",
    },
  ]);

  const categoryTabs = computed<CategoryTab[]>(() => [
    {
      key: "main",
      label: t("history.categories.main"),
      class: "purple",
    },
    {
      key: "additional",
      label: t("history.categories.additional"),
      class: "violet",
    },
    {
      key: "financial",
      label: t("history.categories.financial"),
      class: "green",
    },
  ]);

  function selectTab(tab: FinanceTabKey) {
    activeTab.value = tab;
  }

  function toggleCategory(category: Category) {
    activeCategory.value = activeCategory.value === category ? null : category;
  }

  function normalizeAmount(amount: string | number): number {
    if (typeof amount !== "string") {
      return Number(amount) || 0;
    }

    return Number(amount.replace(/^[-+]/, "")) || 0;
  }

  function calculateCategoryTotals(
    operations: PurchaseHistoryItem[] = apiResponse.value
  ) {
    categoryTotals.value = {
      main: 0,
      additional: 0,
      financial: 0,
    };

    for (const operation of operations) {
      const category = operation?.product?.category;

      if (
        !Object.prototype.hasOwnProperty.call(categoryTotals.value, category)
      ) {
        continue;
      }

      const amount = normalizeAmount(operation.chargedAmount);

      categoryTotals.value[category] += operation.type.includes("minus")
        ? -amount
        : amount;
    }
  }

  function formatInternalNameForApi(internalName: string): string {
    switch (internalName) {
      case "General TopUp":
        return t("history.finances.generalTopUp");
      default:
        return internalName;
    }
  }

  function formatInternalName(internalName: string): string {
    if (internalName === "Top Up Bonus Wallet") {
      return t("history.finances.paymentCredit");
    }

    if (internalName === "Correction") {
      return t("history.finances.balanceCorrection");
    }

    return internalName;
  }

  const groupedOperations = computed<FinanceOperationGroup[]>(() => {
    const groups: Record<string, FinanceOperation[]> = {};
    const { start } = getPeriodStartEnd();
    const startDate = new Date(start);

    apiResponse.value.forEach((operation) => {
      const operationDate = new Date(operation.purchasedOn);

      if (operationDate < startDate) {
        return;
      }

      const [dateKey, timeWithSeconds = ""] = operation.purchasedOn.split("T");
      const normalized: FinanceOperation = {
        type: operation.type.includes("minus") ? "minus" : "plus",
        time: timeWithSeconds.slice(0, 5),
        internal_name: formatInternalNameForApi(
          operation.product.internal_name
        ),
        name: operation.product.name,
        category: operation.product.category,
        amount: normalizeAmount(operation.chargedAmount),
      };

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(normalized);
    });

    return Object.entries(groups)
      .sort((left, right) => (left[0] < right[0] ? 1 : -1))
      .map(([date, operations]) => ({
        date: formatDateToHuman(date),
        operations: operations.sort((left, right) =>
          left.time < right.time ? 1 : -1
        ),
      }));
  });

  const filteredOperations = computed<FinanceOperationGroup[]>(() =>
    groupedOperations.value
      .map((day) => {
        let operations = day.operations;

        if (activeTab.value === "plus") {
          operations = operations.filter(
            (operation) => operation.type === "plus"
          );
        }

        if (activeTab.value === "minus") {
          operations = operations.filter(
            (operation) => operation.type === "minus"
          );
        }

        if (activeCategory.value) {
          operations = operations.filter(
            (operation) => operation.category === activeCategory.value
          );
        }

        return {
          ...day,
          operations,
        };
      })
      .filter((day) => day.operations.length > 0)
  );

  const totalSum = computed(() => {
    const raw = Object.values(categoryTotals.value).reduce(
      (sum, value) => sum + value,
      0
    );
    return Math.round(raw * 100) / 100;
  });

  const visibleCategoryTabs = computed(() => {
    let visibleKeys: Category[] = [];

    if (activeTab.value === "plus") {
      visibleKeys = ["financial"];
    } else if (activeTab.value === "minus") {
      visibleKeys = ["main", "additional"];
    } else {
      visibleKeys = ["main", "additional", "financial"];
    }

    return categoryTabs.value.filter(
      (category) =>
        visibleKeys.includes(category.key) &&
        categoryTotals.value[category.key] !== 0
    );
  });

  const progressBarSegments = computed(() => {
    const visibleCategories = visibleCategoryTabs.value;
    const sums = visibleCategories.map((category) =>
      Math.abs(categoryTotals.value[category.key])
    );
    const total = sums.reduce((sum, value) => sum + value, 0);

    if (total === 0) {
      return [];
    }

    return visibleCategories.map((category) => ({
      key: category.key,
      class: category.class,
      percent: (Math.abs(categoryTotals.value[category.key]) / total) * 100,
    }));
  });

  async function fetchOperations() {
    const { start, end } = getPeriodStartEnd();

    try {
      const response = await getPurchaseHistory(20, 0, start, end);
      apiResponse.value = response.data.response ?? [];
      calculateCategoryTotals(apiResponse.value);
    } catch (error) {
      console.log(error);
    }
  }

  watch(selectedPeriod, fetchOperations);

  onMounted(() => {
    void fetchOperations();
  });

  return {
    activeCategory,
    categoryTotals,
    choosePeriod,
    fetchOperations,
    filteredOperations,
    formatInternalName,
    isCustomPeriod,
    isOpenedSelect,
    openSelect,
    operationsTabs,
    periodOptions,
    periodText,
    progressBarSegments,
    resetPeriod,
    selectTab,
    selectedPeriod,
    toggleCategory,
    totalSum,
    visibleCategoryTabs,
  };
}

export type {
  Category,
  FinanceOperation,
  FinanceOperationGroup,
  FinanceTabKey,
  HistoryPeriodOption,
};
